// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
// eslint-disable-next-line
import Big from 'big.js';
import { getSalesType } from 'shared/helpers/products';

// this function used to live in /bogo/common.js but that caused a circular dependency reference
export const getBogoIndividualDiscount = (reward, individualPrice) => {
  if (reward?.dollarDiscount) {
    const dollarDiscount = Big(reward.dollarDiscount || 0);
    // If the dollar discount would exceed the price of the item, return the price of the item as the discount instead
    return individualPrice.minus(dollarDiscount).gt(0) ? dollarDiscount : individualPrice;
  }
  if (reward?.targetPrice && Big(reward.targetPrice).lt(individualPrice)) {
    const discount = individualPrice.minus(Big(reward.targetPrice || 0));
    return discount.gt(0) ? discount : Big(0);
  }
  return individualPrice.times(Big(reward?.percentDiscount || 0).div(100));
};

/*
  anytime we have a discount that is not percentage based, we want to spread
  that discount evenly over each of the products in the cart. The leftOver value
  represents the fractional cents left over from floating point math and
  we want to roll that into the next product so we don't lose them
 */
export const amortizeDollarDiscount = (couponOrReward, leftOver, applicableSubtotal, compositePrice) => {
  // Take the proportional amount.  Roll fractional cents forward trying to find a home for them.
  const dollarDiscountApplicable = Big(couponOrReward?.dollar || 0)
    .times(compositePrice)
    .div(applicableSubtotal)
    .plus(leftOver);

  const dollarDiscount = dollarDiscountApplicable.round(2);

  return {
    amortizedDiscount: dollarDiscount,
    leftOver: dollarDiscountApplicable.minus(dollarDiscount),
  };
};

// Works for our case of a non 0 length array containing positive numbers.
const findMaxKey = (hash) => {
  let currentMax = Big(0),
    currentMaxIndex = -1;
  const keys = _.keys(hash);
  _.forEach(keys, (key, ind) => {
    const val = Big(hash[key]);
    if (Big(val, 'Find Max Key').gte(currentMax)) {
      currentMax = val;
      currentMaxIndex = ind;
    }
  });
  return keys[currentMaxIndex];
};

export function getSpecialSettings(bogoSpecial, globalSpecialSettings) {
  const specialSettings = globalSpecialSettings || {};
  if (bogoSpecial?.stackingBehavior) {
    specialSettings.stackingBehavior = bogoSpecial.stackingBehavior;
    specialSettings.discountStacking = bogoSpecial.discountStacking;
  }
  if (bogoSpecial?.discountPrecedence) {
    specialSettings.discountPrecedence = bogoSpecial.discountPrecedence;
  }
  return specialSettings;
}

export const prepareProductTotal = (details) => {
  const { productTotal, productTotalRaw } = _.reduce(
    details,
    (accumulator, detail) => {
      const productPrice = Big(detail.mixAndMatch?.adjustedBasePrice || detail.basePrice).times(detail.quantity);
      accumulator.productTotal = accumulator.productTotal.add(productPrice.round(2, 1));
      accumulator.productTotalRaw = accumulator.productTotalRaw.add(productPrice);
      return accumulator;
    },
    { productTotal: Big(0), productTotalRaw: Big(0) }
  );

  return { productTotal, residualPrice: productTotalRaw.minus(productTotal) };
};

// TODO: this should probably move to ./taxes.js
/*
 * For products where it is obvious what the total is we have to make certain that the pennies
 * line up.  There is not right rounding that makes this work as the following example shows.
 * $2 product containing 37% cannabis tax and 9.9% sales tax with all of this in the POS.
 * So at checkout we compute
 * pre-tax price = 2/(1.37 + .0990) = 1.36147
 * sales-tax = (2/(1.37 + .0990))*1.37 = .50374
 * cannabis-tax = (2/(1.37 + .0990))*0.0990 = .13478
 * 1.36 + .50 + .13 = 1.99
 * A penny shy of our $2 and really obvious to the person who pulled the $2 item off the shelf.
 *
 * If we end up more then 1/2 a cent to redistribute then add the extra cents in unit
 * quantities where there is the largest residual (left over after .01).
 */
export const redistributePennies = ({ cannabisTax = 0, salesTax = 0, price = 0 } = {}) => {
  cannabisTax = Big(cannabisTax, 'Redistribute Pennies');
  salesTax = Big(salesTax, 'Redistribute Pennies');
  price = Big(price, 'Redistribute Pennies');
  const residuals = {
    price: price.minus(price.round(2, 0)),
  };
  if (!cannabisTax.eq(0)) {
    residuals.cannabis = cannabisTax.minus(cannabisTax.round(2, 0));
  }
  if (!salesTax.eq(0)) {
    residuals.sales = salesTax.minus(salesTax.round(2, 0));
  }

  const residualTotal = _.reduce(_.values(residuals), (accum, val) => accum.add(val), Big(0));
  const accountableResidual = residualTotal.round(2, 1);
  let pennies = accountableResidual.times(100);
  delete residuals.price; // We want to count the residual from price but not modify the price.
  const residualRemaining = _.reduce(residuals, (accum, val) => accum.add(val), Big(0)).minus(accountableResidual);
  cannabisTax = cannabisTax.round(2, 0);
  salesTax = salesTax.round(2, 0);
  while (pennies.gt(0) && _.keys(residuals).length !== 0) {
    const key = findMaxKey(residuals);
    // Divide them across the types of taxes we have available.
    const thisPass = pennies.div(_.keys(residuals).length).round(0, 3);
    // eslint-disable-next-line default-case
    switch (key) {
      case 'cannabis':
        cannabisTax = cannabisTax.add(thisPass.times(0.01));
        break;
      case 'sales':
        salesTax = salesTax.add(thisPass.times(0.01));
        break;
    }
    residuals[key] = Big(0);
    pennies = pennies.minus(thisPass);
  }
  // we've consumed the extra pennies through this process.
  return { cannabisTax, salesTax, residual: residualRemaining };
};

// this function used to live in ./sales.js but that caused a circular dependency reference
export const getSaleAdjustmentForMenuItem = (detail) => {
  if (!_.isEmpty(detail?.saleAdjustments)) {
    let adjustmentTotal = Big(0);

    _.forEach(detail.saleAdjustments, (adjustment) => {
      const { discount, percentDiscount, targetPrice } = adjustment?.discount?.discountData || {};
      // How much is this item discounted
      let individualDiscount = Big(0);

      if (targetPrice) {
        // Difference between base price and target price
        individualDiscount = Big(detail.menuBasePrice).minus(Big(discount || 0));
      } else if (percentDiscount) {
        // Discount is divided by 100 because the discount is a percentage
        individualDiscount = Big(detail.menuBasePrice).times(Big(discount || 0).div(100));
      } else {
        // Dollar discount means the discount is the discount
        individualDiscount = Big(discount || 0);
      }

      adjustmentTotal = adjustmentTotal.plus(individualDiscount.times(adjustment.count));
    });

    return adjustmentTotal;
  }

  return null;
};

// this function used to live in /bogo/common.js but that caused a circular dependency reference
const getBogoDiscountForMenuItem = ({ detail, specialsSettings, individualMenuSaleAdjustment }) => {
  const { stackingBehavior: globalStackingBehavior } = specialsSettings;

  const { discountTotal: bogoDiscount } = _.reduce(
    detail?.bogoSavings?.individual || [],
    ({ discountTotal, itemPrice }, savingsObj) => {
      const {
        conditionQuantity,
        isDiscountToCartReward,
        maxApplicable,
        stackingBehavior: individualStackingBehavior,
      } = savingsObj;
      const rewardStackingBehaviorIsCumulative = isCumulative(individualStackingBehavior, globalStackingBehavior);

      const totalItemsAddedPastCondition = Math.max(0, detail.quantity - conditionQuantity);
      const discountQuantity = isDiscountToCartReward
        ? maxApplicable
        : Math.min(maxApplicable, totalItemsAddedPastCondition);

      const priceToDiscountFrom = rewardStackingBehaviorIsCumulative ? Big(detail.menuBasePrice) : itemPrice;
      const bogoIndividualDiscount = getBogoIndividualDiscount(savingsObj, priceToDiscountFrom);
      const discountAmount = bogoIndividualDiscount.times(discountQuantity);

      if (isDiscountToCartReward) {
        _.set(detail, `bogoSavings.isDiscountToCartReward`, true);
      }
      return {
        discountTotal: discountTotal.add(discountAmount),
        itemPrice: itemPrice.minus(discountAmount.div(detail.quantity)),
      };
    },
    {
      discountTotal: Big(0),
      itemPrice: Big(detail.menuIndividualPrice).add(individualMenuSaleAdjustment),
    }
  );
  return !_.isNil(bogoDiscount) && bogoDiscount.gt(0) ? bogoDiscount.toFixed(2) : null;
};

export const calculateMenuTotal = (details, specialsSettings) =>
  _.reduce(
    details,
    (accum, detail) => {
      const menuSaleAdjustment = getSaleAdjustmentForMenuItem(detail);
      if (!_.isNil(menuSaleAdjustment)) {
        _.set(detail, `menuSaleAdjustment`, menuSaleAdjustment);
      }
      const individualMenuSaleAdjustment = Big(menuSaleAdjustment || 0).div(detail.quantity);

      const menuBogoDiscount = getBogoDiscountForMenuItem({ detail, specialsSettings, individualMenuSaleAdjustment });
      if (!_.isNil(menuBogoDiscount)) {
        _.set(detail, `bogoSavings.menuBogoDiscount`, menuBogoDiscount);
      }

      return accum.add(
        Big(detail.menuPrice || 0)
          .add(menuSaleAdjustment || 0)
          .minus(menuBogoDiscount || 0)
      );
    },
    Big(0)
  );

export const getOfferRewardDiscountType = (offerReward) => {
  if (offerReward.dollarDiscount) {
    return 'dollarDiscount';
  }
  if (offerReward.targetPrice) {
    return 'targetPrice';
  }
  // default to percent discount
  return 'percentDiscount';
};

export const getSaleDiscountType = (saleSpecial, product) => {
  let discountType;

  // Check for blended sale discount types
  if (product && !_.isEmpty(saleSpecial.specialRestrictions)) {
    const salesType = getSalesType(product);
    // eslint-disable-next-line consistent-return
    _.forEach(saleSpecial.specialRestrictions, (restriction, key) => {
      if (_.includes(salesType, key)) {
        discountType = restriction?.discountType;
        return false; // break early
      }
    });
  }

  if (!discountType) {
    discountType = 'percentDiscount';
    if (saleSpecial.dollarDiscount) {
      discountType = 'dollarDiscount';
    } else if (saleSpecial.targetPrice) {
      discountType = 'targetPrice';
    }
  }

  return discountType;
};

export const sortSpecials = (specials, product) =>
  _.sortBy(specials, (special) => {
    const specialKey = special.sourceId || special.specialId || special._id;

    const specialTypeKey = special.specialType === 'bogo' ? 1 : 0;

    const stackingKey = special?.stackingMode === 'stacking' || special.discountStacking === true ? 1 : 0;

    const discountType =
      specialTypeKey === 1 ? getOfferRewardDiscountType(special) : getSaleDiscountType(special, product);
    let discountKey = 1; // percent discounts
    if (discountType === 'dollarDiscount') {
      discountKey = 0;
    } else if (discountType === 'targetPrice') {
      discountKey = 2;
    }

    return `${specialTypeKey}_${stackingKey}_${discountKey}_${specialKey}`;
  });

export const findAPlaceForResidualPennies = (
  avoidTaxes,
  productTotal,
  residualPrice,
  cannabisTaxTotal,
  salesTaxTotal,
  receipt
) => {
  /*
   * Since we do taxes on a product by product basis we end up with bits of pennies between
   * products that might add up in the end.  If we didn't try to break out taxes and have
   * products which can be on special and have those taxes taken at various points then we
   * could just compute a total and take any odd pennies not directly associated with a product
   * and apply them.  But with our varying treatment of when taxes are shown and how specials are
   * dealt with we just keep track of the residuals and add them back here.
   *
   * Note we should probably really distribute whatever extra pennies we have proportionally between
   * sales and excise tax.  There really shouldn't be more then +/- 1 so just tack it onto the cannabis
   * tax which is likely to be the largest one anyways.
   */
  if (avoidTaxes) {
    // If we are in the odd taxesFirst and we have taxes on the menu case then we don't
    // have separate taxes.  Just add them to the product total.
    productTotal = productTotal.add(residualPrice.round(2, 1));
  } else {
    // Don't create a tax category by adding a bit of residual to 0.
    // eslint-disable-next-line no-lonely-if
    if (cannabisTaxTotal.gt(0)) {
      cannabisTaxTotal = cannabisTaxTotal.add(residualPrice.round(2, 1));
    } else if (salesTaxTotal.gt(0)) {
      salesTaxTotal = salesTaxTotal.add(residualPrice.round(2, 1));
    }
    // If we couldn't find a place for it in taxes then stacking it on top of the
    // product total can only create something odd like a product you thought
    // was 48 but came out as 47.99 because of underflow on a discount calculation.
  }

  // Find some taxes to put the residual cents again.  Do this by sorting the
  // taxes and then applying cents from the largest downwards.
  let cents = residualPrice.times(100).round(0);
  if (!cents.eq(0)) {
    const taxValues = _.reverse(
      _.sortBy(
        _.filter(
          _.compact(_.flatMap(receipt.details.products, (detail) => [detail.taxes?.sales, detail.taxes?.cannabis])),
          (val) => !val.eq(0)
        ),
        (val) => Number(val)
      )
    );
    if (taxValues.length > 0) {
      const increment = Number(cents.div(taxValues.length).round(0, 3));
      cents = Number(cents);
      while (cents !== 0) {
        const value = taxValues.shift();
        let found = false;
        // eslint-disable-next-line no-restricted-syntax
        for (const product of receipt.details.products) {
          // eslint-disable-next-line no-restricted-syntax
          for (const tax of [`cannabis`, `sales`]) {
            product.taxes = product.taxes || {};
            if (product.taxes[tax] === value) {
              const amount = Math.min(increment, cents);
              product.taxes[tax] = product.taxes[tax].plus(amount);
              cents -= amount;
              found = true;
              break;
            }
          }
          if (found) {
            break;
          }
        }
      }
    }
    // Same as above.  If we couldn't find a place for this cent in the taxes then there
    // really isn't a reasonable place to put it.
  }
  return { productTotal, cannabisTaxTotal, salesTaxTotal };
};

export const calculateManualDiscounts = (manualDiscount, total, receipt) => {
  const { details, taxConfig } = receipt;
  const { discountTaxOrder } = taxConfig;
  const { productTotal } = prepareProductTotal([...details.products]);
  const productTotalInDollars = Big(productTotal, `product total`).div(100);
  let newCredit;
  if (manualDiscount.fixedDiscountInCents) {
    const discount = Big(manualDiscount.fixedDiscountInCents, `fixed discount`).div(100);
    newCredit = discount.gt(total) ? total : discount;
  } else if (discountTaxOrder !== `discountsFirst` && discountTaxOrder !== `both`) {
    newCredit = total.times(Big(manualDiscount.percentDiscount, `manual percent discount`).div(100)).round(2, 2);
  } else {
    newCredit = Big(productTotalInDollars)
      .times(Big(manualDiscount.percentDiscount, `manual percent discount`).div(100))
      .round(2, 2);
  }
  receipt.addDiscount({ type: `manual`, value: newCredit.times(100) });
  return { newCredit };
};

export const calculateTip = (
  shouldApplyTip,
  tipValue,
  negativePosttaxSubtotal,
  altBasisForZeroedSubtotal,
  posttaxSubtotal
) => {
  let tip = Big(0);
  if (shouldApplyTip) {
    if (_.isArray(tipValue)) {
      [tipValue] = tipValue;
    }
    if (tipValue.percent) {
      // Use product total instead of subtotal, if a discount has brought the subtotal to a negative
      // value and we zeroed it above.
      tip = Big(tipValue.percent, 'Tip Value Percent').times(
        negativePosttaxSubtotal ? altBasisForZeroedSubtotal : posttaxSubtotal
      );
    } else if (tipValue.dollar) {
      tip = Big(tipValue.dollar, 'Tip Value Dollar');
    }
  }
  return tip;
};

export const calculatePostTaxSubtotal = (
  productTotal,
  salesTaxTotal,
  cannabisTaxTotal,
  bottleDepositTaxCentsTotal,
  credit
) => {
  let posttaxSubtotal = productTotal
    .plus(salesTaxTotal)
    .plus(cannabisTaxTotal)
    .plus(bottleDepositTaxCentsTotal.div(100))
    .minus(credit);

  // handle edge case where a coupon discounts that exceed the subtotal results in a negative subtotal
  const negativePosttaxSubtotal = posttaxSubtotal.lt(0);
  if (negativePosttaxSubtotal) {
    posttaxSubtotal = new Big(0);
  }
  return { posttaxSubtotal, negativePosttaxSubtotal };
};

export const orderProductsForDiscountBehavior = (products = [], specialsSettings) => {
  const { discountStacking, stackingBehavior, nonStackingBehavior } = specialsSettings;
  const discountBehavior = discountStacking ? stackingBehavior : nonStackingBehavior;
  if (!discountBehavior) {
    // this will be hit when an individual special has no discount behavior
    // in that case we want to preserve the order for later calcs
    return products;
  }
  // the order of the products will determine which is discounted so if favor customer
  // is enabled we want the most expensive products first and so the larger discount will
  // be applied
  return _.sortBy(products, ({ menuIndividualPrice = 0 }) =>
    discountBehavior === 'favorCustomer' ? -Number(menuIndividualPrice) : Number(menuIndividualPrice)
  );
};

export const orderProductsForDiscountPrecedence = (products = [], specialsSettings, context) => {
  const { discountPrecedence } = specialsSettings;

  if (!discountPrecedence) {
    // this will be hit when an individual special has no discount precedence
    // in that case we want to preserve the order for later calcs
    return products;
  }

  if (context === 'rewards') {
    return _.sortBy(products, ({ menuIndividualPrice = 0 }) =>
      discountPrecedence === 'highToLow' || discountPrecedence === 'mostExpensiveItem'
        ? -Number(menuIndividualPrice)
        : Number(menuIndividualPrice)
    );
  }

  // context === 'conditions'
  return _.sortBy(products, ({ menuIndividualPrice = 0 }) =>
    discountPrecedence === 'highToLow' || discountPrecedence === 'mostExpensiveItem'
      ? Number(menuIndividualPrice)
      : -Number(menuIndividualPrice)
  );
};

export const getGlobalSpecialsSettings = (dispensary) => {
  if (_.isNil(dispensary.specialsSettings)) {
    return {
      discountStacking: true,
      discountPrecedence: 'highToLow',
      stackingBehavior: 'compounding',
      nonStackingBehavior: 'favorCustomer',
    };
  }
  const { specialsSettings } = dispensary;

  const stackingBehavior =
    specialsSettings.discountStacking === true
      ? specialsSettings.stackingBehavior || specialsSettings.discountBehavior
      : 'cumulative'; // 'cumulative' is the default when discount stacking is disabled

  const nonStackingBehavior =
    dispensary.specialsSettings.discountStacking === false
      ? specialsSettings.nonStackingBehavior || specialsSettings.discountBehavior
      : specialsSettings.nonStackingBehavior || 'favorCustomer'; // 'favorCustomer' is the default

  const discountPrecedence =
    specialsSettings.discountPrecedence || (nonStackingBehavior === 'favorCustomer' ? 'highToLow' : 'lowToHigh');

  return {
    stackingBehavior,
    nonStackingBehavior,
    discountStacking: specialsSettings.discountStacking,
    discountPrecedence,
  };
};

export const isCumulative = (individualStackingBehavior, globalStackingBehavior) => {
  if (individualStackingBehavior) {
    return individualStackingBehavior === 'cumulative';
  }
  return globalStackingBehavior === 'cumulative';
};
